import React, {useContext, useEffect, useState} from 'react';
import { Card, Divider } from 'antd';
import get from 'lodash/get';
import { stringToUIString } from '../utils/dateUtils';
import { formatMoneyAmount } from '../utils/formatUtils';
import { GlobalStateContext } from '../context/GlobalStateProvider';
import CircleCheck from './svg/CircleCheckSVG';
import { getPolicyHashForPortal } from '../api/applicationAPI';
import {env} from '../env';
import {GA_CONFIGURATION, sendGoogleAnalyticsEvent} from "../utils/googleAnalyticsUtils";

function replaceEnvValue(url) {
  const iLeft = url.indexOf('{');
  const iRight = ((iLeft >= 0) ? url.indexOf('}') : iLeft);
  let result = url;

  if (iLeft < iRight) {
    // Extract the substring and look for it in the env structure
    let key = url.substring((iLeft + 1), iRight);
    let lookup = key;
    if ( ! {}.hasOwnProperty.call(env, lookup)) {
      lookup = (key[0].toLowerCase() + key.substring(1));
    }
    if ({}.hasOwnProperty.call(env, lookup)) {  // ask ESLint
      result = url.replace('{' + key + '}', env[lookup]);
    }
  }

  return (result);
}

export const PaymentSuccessful = ({ actions }) => {
  if (!history.state) return null;

  const POLICY_HASH = '<PolicyHash>';
  const REGISTRATION_HASH = '<RegistrationHash>';
  const POLICY_NUMBER = '<PolicyNumber>';
  const POLICY_ID = '<PolicyId>';

  const data = history.state.data;
  const paymentSource = data.basicPolicy.electronicPaymentSource;

  const globalState = useContext(GlobalStateContext);
  let [ policyHashKey, setPolicyHashKey ] = useState('');
  let [ registrationHashKey, setRegistrationHashKey ] = useState('');

  const achHeader = [
    {
      label: 'Payment Type',
      value: 'Bank',
    },
    {
      label: 'Bank Name',
      mapping: 'basicPolicy.electronicPaymentSource.achBankName',
    },
    {
      label: 'Account Number',
      mapping: 'basicPolicy.electronicPaymentSource.achBankAccountNumber',
    },
    {
      label: 'Routing Number',
      mapping: 'basicPolicy.electronicPaymentSource.achRoutingNumber',
    },
    {
      label: 'Amount Paid',
      mapping: 'transactionInfo.paymentAmt',
      formatValue: true,
    },
    // Once this info becomes available, update the mapping with the correct path and it will show on the UI
    // {
    //   label: 'Transaction ID',
    //   value: '<transaction id>',
    //   // mapping: 'transactionInfo.id'
    // },
  ];

  const ccHeader = [
    {
      label: 'Payment Type',
    },
    {
      label: 'Card Type',
    },
    {
      label: 'Name on Card',
    },
    {
      label: 'Amount Paid',
    },
    {
      label: 'Transaction ID',
    },
  ];

  const { isDesktop, isMobile, isTablet, changePaymentLayout } = globalState;

  useEffect(() => {
    if ( ! policyHashKey && ! registrationHashKey) {
      getPolicyHashForPortal(data.policyNumber).then(result => {
        setPolicyHashKey(result.resourceToken);
        setRegistrationHashKey(result.registrationToken);
      });
    }
  }, [ policyHashKey, registrationHashKey ]);

  const populateUrls = (linkList) => {

    let resultList = [ ];
    for (let link of linkList) {
      let url = link.url;
      if (url.indexOf(POLICY_NUMBER) > 0) {
        url = url.replace(POLICY_NUMBER, data.policyNumber);
      } else if ( (policyHashKey && (url.indexOf(POLICY_HASH) > 0))
      || (registrationHashKey && (url.indexOf(REGISTRATION_HASH) > 0)) ) {
        policyHashKey ? url = url.replace(POLICY_HASH, policyHashKey) : null;
        registrationHashKey ? url = url.replace(REGISTRATION_HASH, registrationHashKey): null;
      } else if (url.indexOf(POLICY_ID) > 0) {
        url = url.replace(POLICY_ID, data.policyRef);
      }
      resultList.push(replaceEnvValue(url));
    }

    return (resultList);
  }

  const getCardWidth = () => {
    if (isDesktop) {
      return '70%';
    }
    if (isTablet && !changePaymentLayout) {
      return '90%';
    }
    if (changePaymentLayout) {
      return '100%';
    }
  };

  const styles = {
    cardContainer: {
      width: getCardWidth(),
      margin: changePaymentLayout ? '' : 'auto',
      paddingTop: '32px',
      paddingBottom: '32px',
    },
    container: {
      display: 'flex',
      justifyContent: 'center',
      alignContent: 'center',
      alignItems: 'center',
    },
    divider: {
      marginTop: '16px',
      marginBottom: '16px',
    },
    dividerContainer: {
      margin: 'auto',
      width: isTablet ? '85%' : '65%',
    },
    sectionContainer: {
      margin: 'auto',
      width: isTablet ? '75%' : '50%',
    },
    spacedContainer: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    text: {
      margin: 0,
      paddingBottom: '4px',
    },
    titleContainer: {
      marginTop: '16px',
      alignItems: 'center',
      flexDirection: 'column',
    },
    titleText: {
      color: '#1f8446',
      fontWeight: 'bold',
    },
  };

  const Header = ({ data, headerData }) => {
    if (headerData === null) return null;

    return (
      <div>
        {headerData.map((rowItem, index) => {
          const { label, value, mapping, formatValue } = rowItem;

          const rowValue = value ? value : get(data, mapping);

          return (
            <div style={styles.spacedContainer} key={index}>
              <p style={styles.text}>{label}</p>
              <p style={{ fontWeight: 'bold', ...styles.text }}>
                {formatValue && formatValue === true
                  ? formatMoneyAmount(parseFloat(rowValue))
                  : rowValue}
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  const PolicySummary = ({ data }) => {
    const customerInfo = data.insured.partyInfo[0];
    const { nameInfo } = customerInfo;
    const customerNumber = data.customerRef;

    const startDate = stringToUIString(data.basicPolicy.effectiveDt);
    const endDate = stringToUIString(data.basicPolicy.expirationDt);

    const total =
      parseFloat(data.basicPolicy.writtenPremiumAmt) +
      parseFloat(data.basicPolicy.writtenFeeAmt);

    return (
      <section style={styles.sectionContainer}>
        <div>
          <h2 style={{ fontWeight: 'bold' }}>Policy Summary</h2>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'column',
            marginTop: '8px',
          }}
        >
          <h3 style={{ fontWeight: 'bold' }}>Customer Information</h3>
          <div>
            <div style={styles.spacedContainer}>
              <p style={styles.text}>Customer Name</p>
              <p style={{ fontWeight: 'bold', ...styles.text }}>
                {nameInfo.givenName} {nameInfo.surname}
              </p>
            </div>
            <div style={styles.spacedContainer}>
              <p style={styles.text}>Customer Number</p>
              <p style={{ fontWeight: 'bold', ...styles.text }}>
                {customerNumber}
              </p>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'column',
            marginTop: '8px',
          }}
        >
          <h3 style={{ fontWeight: 'bold' }}>Policy Information</h3>
          <div>
            <div style={styles.spacedContainer}>
              <p style={styles.text}>Policy Number</p>
              <p style={{ fontWeight: 'bold', ...styles.text }}>
                {data.policyNumber}
              </p>
            </div>
            <div style={styles.spacedContainer}>
              <p style={styles.text}>Policy Term</p>
              <p style={{ fontWeight: 'bold', ...styles.text }}>
                {startDate} - {endDate}
              </p>
            </div>
            <div style={styles.spacedContainer}>
              <p style={styles.text}>Premium</p>
              <p style={{ fontWeight: 'bold', ...styles.text }}>
                {formatMoneyAmount(total)}/{data.basicPolicy.renewalTermCd}
              </p>
            </div>
            <div style={styles.spacedContainer}>
              <p style={styles.text}>Payment Plan</p>
              <p style={{ fontWeight: 'bold', ...styles.text }}>
                {data.basicPolicy.payPlanCd}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const ActionsSection = ({ actions }) => {
    const handleGoogleLinkAccountEvent = ({googleEventNameTag})=>{
      if (googleEventNameTag === 'LinkAccountRequest'){
        sendGoogleAnalyticsEvent({...GA_CONFIGURATION.Link_Account_Request})
      }
    }
    return (
      <section style={styles.sectionContainer}>
        <div>
          <h2 style={{ fontWeight: 'bold' }}>{actions.title.value}</h2>
          {actions.description.length > 0
            ? actions.description.map((text, index) => (
                <p key={index} style={styles.text}>
                  {text.value}
                </p>
              ))
            : null}
        </div>

        <div style={{ marginTop: '24px' }}>
          {actions.links.length > 0
            ? actions.links.map((link, index) => {
                const { title, url, description } = link;
                return (
                  <div key={index} style={{ marginTop: '8px' }}>
                    <h3 style={{ margin: 0 }}>
                      <a href={urlsMapped[index]} onClick={handleGoogleLinkAccountEvent} target="_blank">
                        {title}
                      </a>
                    </h3>
                    <p style={styles.text}>{description}</p>
                  </div>
                );
              })
            : null}
        </div>
      </section>
    );
  };

  const urlsMapped = populateUrls(actions.links);
  return (
    <div className="gray-background">
      <Card style={styles.cardContainer}>
        <section style={styles.sectionContainer}>
          <div style={{ ...styles.container, flexDirection: 'column' }}>
            <CircleCheck color={'#1f8446'} />
            <div style={{ ...styles.container, flexDirection: 'column' }}>
              <h1 style={styles.titleText}>Payment Successful!</h1>
              <h3>Thank you for your payment!</h3>
            </div>
          </div>

          <Header
            data={data}
            headerData={paymentSource.methodCd === 'ACH' ? achHeader : null}
          />
        </section>

        <div style={styles.dividerContainer}>
          <Divider style={styles.divider} />
        </div>

        <PolicySummary data={data} />

        <div style={styles.dividerContainer}>
          <Divider style={styles.divider} />
        </div>

        <ActionsSection actions={actions} />
      </Card>
    </div>
  );
};
